<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <!-- ================= FORM (create / edit) ================= -->
      <md-card v-if="mode !== 'list'" class="user-form">
        <md-card-header class="form-header">
          <md-button class="md-icon-button md-simple" @click="closeForm">
            <md-icon>arrow_back</md-icon>
          </md-button>
          <div>
            <h3 class="title">{{ mode === "create" ? "New user" : "Edit user" }}</h3>
            <p v-if="mode === 'edit'" class="category">{{ editing.email }}</p>
          </div>
        </md-card-header>

        <md-card-content>
          <form novalidate @submit.prevent="save">
            <!-- Access level -->
            <section class="form-section">
              <h4 class="section-title">Access level</h4>

              <div class="role-grid">
                <label
                  v-for="r in roles"
                  :key="r.value"
                  class="role-option"
                  :class="['role-option--' + r.value, { 'is-selected': form.role === r.value }]"
                >
                  <input v-model="form.role" type="radio" name="role" :value="r.value" />
                  <md-icon class="role-option__icon">{{ r.icon }}</md-icon>
                  <span class="role-option__title">{{ r.label }}</span>
                  <span class="role-option__hint">{{ r.hint }}</span>
                </label>
              </div>

              <div v-if="form.role === 'vendor'" class="select-field" :class="{ 'has-error': errors.company }">
                <label>Vendor</label>
                <v-select
                  v-model="form.customer"
                  :options="customers"
                  label="name"
                  :clearable="false"
                  :append-to-body="true"
                  placeholder="Choose a vendor"
                />
                <span v-if="errors.company" class="field-error">{{ errors.company }}</span>
              </div>

              <div v-if="form.role === 'subVendor'" class="select-field" :class="{ 'has-error': errors.company }">
                <label>Sub vendor</label>
                <v-select
                  v-model="form.client"
                  :options="clients"
                  label="name"
                  :clearable="false"
                  :append-to-body="true"
                  placeholder="Choose a sub vendor"
                />
                <span v-if="errors.company" class="field-error">{{ errors.company }}</span>
              </div>
            </section>

            <!-- Account details -->
            <section class="form-section">
              <h4 class="section-title">Account details</h4>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-50 md-small-size-100">
                  <md-field :class="{ 'md-invalid': errors.fullname }">
                    <label>Full name</label>
                    <md-input v-model.trim="form.fullname" required />
                    <span class="md-error">{{ errors.fullname }}</span>
                  </md-field>
                </div>

                <div class="md-layout-item md-size-50 md-small-size-100">
                  <md-field :class="{ 'md-invalid': errors.email }">
                    <label>Email address</label>
                    <md-input v-model.trim="form.email" type="email" required :disabled="mode === 'edit'" />
                    <span v-if="mode === 'edit'" class="md-helper-text">
                      Email is the sign-in ID and can't be changed here.
                    </span>
                    <span class="md-error">{{ errors.email }}</span>
                  </md-field>
                </div>

                <div v-if="mode === 'create'" class="md-layout-item md-size-50 md-small-size-100">
                  <md-field :class="{ 'md-invalid': errors.password }">
                    <label>Temporary password</label>
                    <md-input v-model="form.password" type="password" autocomplete="new-password" required />
                    <span class="md-helper-text">At least 6 characters.</span>
                    <span class="md-error">{{ errors.password }}</span>
                  </md-field>
                </div>
              </div>
            </section>

            <!-- Notifications -->
            <section v-if="mode === 'create' || form.role === 'subVendor'" class="form-section">
              <h4 class="section-title">Notifications</h4>
              <div class="checkbox-stack">
                <md-checkbox v-if="mode === 'create'" v-model="form.sendUserOnBoarding">
                  Send onboarding email
                </md-checkbox>
                <md-checkbox v-if="form.role === 'subVendor'" v-model="form.willReceiveDailyReport">
                  Send daily report
                </md-checkbox>
              </div>
            </section>
          </form>
        </md-card-content>

        <md-card-actions md-alignment="right" class="form-actions">
          <md-button class="md-simple" :disabled="saving" @click="closeForm">Cancel</md-button>
          <md-button class="md-primary md-round" :disabled="saving" @click="save">
            {{ saving ? "Saving…" : mode === "create" ? "Create user" : "Save changes" }}
          </md-button>
        </md-card-actions>
      </md-card>

      <!-- ================= LIST ================= -->
      <md-card v-else>
        <md-card-header class="list-header">
          <div>
            <h3 class="title">Users</h3>
            <p class="category">{{ rows.length }} accounts</p>
          </div>
          <div class="list-header__actions">
            <md-field class="search-field" md-clearable>
              <md-icon>search</md-icon>
              <md-input v-model="searchQuery" type="search" placeholder="Search name, email or company" />
            </md-field>
            <md-button class="md-primary md-round" @click="openCreate">
              <md-icon>person_add</md-icon> New user
            </md-button>
          </div>
        </md-card-header>

        <md-card-content>
          <md-table
            v-model="pageRows"
            :md-sort.sync="currentSort"
            :md-sort-order.sync="currentSortOrder"
            :md-sort-fn="keepOrder"
            class="table-hover"
          >
            <md-table-empty-state
              md-icon="group"
              :md-label="searchQuery ? 'No matching users' : 'No users yet'"
              :md-description="searchQuery ? 'Try a different search term.' : 'Create the first user to get started.'"
            />

            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Name" md-sort-by="fullname">
                <span class="cell-name">{{ item.fullname }}</span>
              </md-table-cell>
              <md-table-cell md-label="Email" md-sort-by="email">{{ item.email }}</md-table-cell>
              <md-table-cell md-label="Access" md-sort-by="roleLabel">
                <span class="role-badge" :class="'role-badge--' + item.role">{{ item.roleLabel }}</span>
                <span v-if="item.company" class="cell-company">{{ item.company }}</span>
              </md-table-cell>
              <md-table-cell md-label="" class="cell-actions">
                <md-button class="md-icon-button md-simple" @click.native="openEdit(item)">
                  <md-icon translate="no">edit</md-icon>
                  <md-tooltip>Edit</md-tooltip>
                </md-button>
                <md-button class="md-icon-button md-simple btn-danger" @click.native="remove(item)">
                  <md-icon translate="no">delete_outline</md-icon>
                  <md-tooltip>Delete</md-tooltip>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>

        <md-card-actions md-alignment="space-between" class="list-footer">
          <p class="card-category">
            <template v-if="total">Showing {{ from + 1 }}–{{ to }} of {{ total }}</template>
          </p>
          <pagination
            v-model="pagination.currentPage"
            class="pagination-no-border pagination-success"
            :per-page="pagination.perPage"
            :total="total"
          />
          <md-field class="per-page">
            <label for="per-page">Per page</label>
            <md-select id="per-page" v-model="pagination.perPage" name="per-page">
              <md-option v-for="n in pagination.perPageOptions" :key="n" :value="n">{{ n }}</md-option>
            </md-select>
          </md-field>
        </md-card-actions>
      </md-card>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination";
import Swal from "sweetalert2";
import { db, functions, fieldValue } from "@/config/firebaseInit";

const ROLES = [
  { value: "qa", label: "QA Employee", hint: "Diamond Services USA staff", icon: "assignment_turned_in" },
  { value: "superAdmin", label: "Super Admin", hint: "Full access to everything", icon: "verified_user" },
  { value: "vendor", label: "Vendor", hint: "Vendor portal access", icon: "business" },
  { value: "subVendor", label: "Sub Vendor", hint: "Sub vendor portal access", icon: "store" },
];
const ROLE_LABELS = ROLES.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

const QA_LIST = "attributes/qa_associates";

const emptyForm = () => ({
  role: "qa",
  fullname: "",
  email: "",
  password: "",
  client: null,
  customer: null,
  sendUserOnBoarding: false,
  willReceiveDailyReport: false,
});

const emptyErrors = () => ({ fullname: "", email: "", password: "", company: "" });

/**
 * Work out a user's role from the stored flags.
 * Older records can carry several flags at once (the old form never cleared
 * them), so precedence matters here.
 */
function roleOf(admin) {
  if (admin.isSuperAdmin) return "superAdmin";
  if (admin.isCustomer && admin.customerID) return "vendor";
  if (admin.isClient) return "subVendor";
  if (admin.isCustomer) return "vendor";
  return "qa";
}

export default {
  components: { Pagination },

  data() {
    return {
      roles: ROLES,
      mode: "list", // "list" | "create" | "edit"
      saving: false,

      admins: [], // bound live to the `administrator` collection
      clients: [],
      customers: [],

      form: emptyForm(),
      errors: emptyErrors(),
      editing: null, // snapshot of the user being edited: { id, fullname, email, role }

      searchQuery: "",
      currentSort: "fullname",
      currentSortOrder: "asc",
      pagination: {
        perPage: 25,
        currentPage: 1,
        perPageOptions: [10, 25, 50, 100],
      },

      addUser: functions.httpsCallable("addUser"),
    };
  },

  computed: {
    clientsById() {
      return Object.fromEntries(this.clients.map((c) => [c.id, c]));
    },
    customersById() {
      return Object.fromEntries(this.customers.map((c) => [c.id, c]));
    },

    // Admin docs plus derived display fields.
    rows() {
      return this.admins.map((a) => {
        const role = roleOf(a);
        let company = "";
        if (role === "subVendor") company = (this.clientsById[a.clientID] || {}).name || a.clientID || "";
        if (role === "vendor") company = (this.customersById[a.customerID] || {}).name || "";
        return { ...a, id: a.id, role, roleLabel: ROLE_LABELS[role], company };
      });
    },

    filteredRows() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter((r) =>
        [r.fullname, r.email, r.company].some((v) => (v || "").toLowerCase().includes(q))
      );
    },

    // Sort the whole list before paging, not just the visible page.
    sortedRows() {
      const key = this.currentSort;
      const dir = this.currentSortOrder === "desc" ? -1 : 1;
      return [...this.filteredRows].sort(
        (a, b) => dir * String(a[key] || "").localeCompare(String(b[key] || ""), undefined, { sensitivity: "base" })
      );
    },

    total() {
      return this.sortedRows.length;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      return Math.min(this.from + this.pagination.perPage, this.total);
    },
    pageRows: {
      get() {
        return this.sortedRows.slice(this.from, this.to);
      },
      set() {
        /* md-table emits sorted copies; sorting is handled in sortedRows */
      },
    },
  },

  watch: {
    searchQuery() {
      this.pagination.currentPage = 1;
    },
    "pagination.perPage"() {
      this.pagination.currentPage = 1;
    },
    "form.role"() {
      this.errors.company = "";
    },
  },

  async created() {
    try {
      await Promise.all([
        this.$bind("admins", db.collection("administrator")),
        this.loadClients(),
        this.loadCustomers(),
      ]);
    } catch (err) {
      Swal.fire("Error", `Couldn't load users: ${err.message}`, "error");
    }
  },

  methods: {
    keepOrder(rows) {
      return rows;
    },

    async loadClients() {
      const snap = await db.collection("clients").get();
      this.clients = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    },

    async loadCustomers() {
      const snap = await db.collection("customers").get();
      this.customers = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    },

    // ---------- form open / close ----------
    openCreate() {
      this.form = emptyForm();
      this.errors = emptyErrors();
      this.editing = null;
      this.mode = "create";
    },

    openEdit(row) {
      // Copy into a separate form so edits don't leak into the live table before saving.
      this.editing = { id: row.id, fullname: row.fullname, email: row.email, role: row.role };
      this.form = {
        ...emptyForm(),
        role: row.role,
        fullname: row.fullname || "",
        email: row.email || "",
        client: this.clientsById[row.clientID] || null,
        customer: this.customersById[row.customerID] || null,
        willReceiveDailyReport: !!row.willReceiveDailyReport,
      };
      this.errors = emptyErrors();
      this.mode = "edit";
    },

    closeForm() {
      this.mode = "list";
      this.form = emptyForm();
      this.errors = emptyErrors();
      this.editing = null;
    },

    // ---------- validation ----------
    validate() {
      const f = this.form;
      const e = emptyErrors();

      if (!f.fullname) e.fullname = "Full name is required";
      if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email address";
      if (this.mode === "create" && f.password.length < 6) e.password = "Password must be at least 6 characters";
      if (f.role === "vendor" && !f.customer) e.company = "Choose a vendor";
      if (f.role === "subVendor" && !f.client) e.company = "Choose a sub vendor";

      this.errors = e;
      return Object.values(e).every((msg) => !msg);
    },

    // Role flags written to Firestore. Every flag is set explicitly so
    // switching roles can't leave stale flags behind.
    rolePayload(f) {
      const base = {
        isAdmin: false,
        isSuperAdmin: false,
        isClient: false,
        isCustomer: false,
        clientID: "",
        customerID: "",
      };
      switch (f.role) {
        case "superAdmin":
          return { ...base, isAdmin: true, isSuperAdmin: true };
        case "subVendor":
          return { ...base, isClient: true, clientID: f.client.id };
        case "vendor":
          return { ...base, isCustomer: true, customerID: f.customer.id, clientID: f.customer.clientID || "" };
        default:
          return { ...base, isAdmin: true };
      }
    },

    // ---------- save ----------
    async save() {
      if (this.saving || !this.validate()) return;
      this.saving = true;
      try {
        if (this.mode === "create") await this.createUser();
        else await this.updateUser();
        this.closeForm();
      } catch (err) {
        Swal.fire("Error", err.message, "error");
      } finally {
        this.saving = false;
      }
    },

    async createUser() {
      const f = this.form;
      const email = f.email.toLowerCase();

      const { data: uid } = await this.addUser({
        email,
        emailVerified: true,
        password: f.password,
        displayName: f.fullname,
        disabled: false,
      });

      const batch = db.batch();
      // NOTE: the password is deliberately NOT stored in Firestore.
      batch.set(db.collection("administrator").doc(uid), {
        uid,
        fullname: f.fullname,
        email,
        phone: "",
        ...this.rolePayload(f),
        sendUserOnBoarding: f.sendUserOnBoarding,
        willReceiveDailyReport: f.role === "subVendor" && f.willReceiveDailyReport,
      });
      if (f.role === "qa") {
        batch.update(db.doc(QA_LIST), { employees: fieldValue.arrayUnion(f.fullname) });
      }
      await batch.commit();

      Swal.fire("User created", `${f.fullname} can now sign in.`, "success");
    },

    async updateUser() {
      const f = this.form;
      const prev = this.editing;

      const payload = { fullname: f.fullname, ...this.rolePayload(f) };
      if (f.role === "subVendor") payload.willReceiveDailyReport = f.willReceiveDailyReport;

      const batch = db.batch();
      batch.update(db.collection("administrator").doc(prev.id), payload);

      // Keep the QA associates list in sync with role and name changes.
      const wasQa = prev.role === "qa";
      const isQa = f.role === "qa";
      const renamed = prev.fullname !== f.fullname;
      const qaRef = db.doc(QA_LIST);
      if (wasQa && (!isQa || renamed)) batch.update(qaRef, { employees: fieldValue.arrayRemove(prev.fullname) });
      if (isQa && (!wasQa || renamed)) batch.update(qaRef, { employees: fieldValue.arrayUnion(f.fullname) });

      await batch.commit();
      Swal.fire("Saved", `${f.fullname} was updated.`, "success");
    },

    // ---------- delete ----------
    async remove(row) {
      const { value: confirmed } = await Swal.fire({
        title: `Delete ${row.fullname}?`,
        text: "They will lose access immediately. This can't be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Delete user",
      });
      if (!confirmed) return;

      try {
        const batch = db.batch();
        batch.delete(db.collection("administrator").doc(row.id));
        if (row.isAdmin) {
          batch.update(db.doc(QA_LIST), { employees: fieldValue.arrayRemove(row.fullname) });
        }
        await batch.commit();
        // TODO: this removes the profile only. The Firebase Auth account still exists
        // until a callable such as `deleteUser` removes it on the server.
        Swal.fire("Deleted", `${row.fullname} was removed.`, "success");
      } catch (err) {
        Swal.fire("Error", err.message, "error");
      }
    },
  },
};
</script>

<style lang="css" scoped>
/* ---------- headers ---------- */
.list-header,
.form-header {
  display: flex;
  align-items: center;
  gap: 16px;
}
.list-header {
  justify-content: space-between;
  flex-wrap: wrap;
}
.list-header .title,
.form-header .title {
  margin: 0;
}
.list-header .category,
.form-header .category {
  margin: 2px 0 0;
  color: #888;
}
.list-header__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.search-field {
  width: 280px;
  margin: 0;
}

/* ---------- form ---------- */
.form-section {
  padding: 8px 0 20px;
  border-bottom: 1px solid #eee;
}
.form-section:last-child {
  border-bottom: 0;
}
.section-title {
  margin: 8px 0 14px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #777;
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}
.role-option {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1.5px solid #e2e2e2;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}
.role-option:hover {
  border-color: #bbb;
}
.role-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.role-option.is-selected {
  border-color: #4caf50;
  background: #f3faf3;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.12);
}
.role-option input:focus-visible + .role-option__icon {
  outline: 2px solid #4caf50;
  outline-offset: 2px;
}
.role-option__icon {
  margin: 0 0 4px !important;
  color: #999 !important;
}
.role-option.is-selected .role-option__icon {
  color: #4caf50 !important;
}
.role-option__title {
  font-weight: 600;
}
.role-option__hint {
  font-size: 12px;
  color: #888;
}

.select-field {
  margin-top: 18px;
  max-width: 420px;
}
.select-field > label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #666;
}
.select-field.has-error >>> .vs__dropdown-toggle {
  border-color: #f44336;
}
.field-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #f44336;
}

.checkbox-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-actions,
.list-footer {
  border: 0;
  margin: 0 20px;
}

/* ---------- table ---------- */
.cell-name {
  font-weight: 500;
}
.cell-company {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #888;
}
.cell-actions {
  text-align: right;
  white-space: nowrap;
}
.btn-danger .md-icon {
  color: #e53935 !important;
}

.role-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}
.role-badge--qa {
  background: #e8f5e9;
  color: #2e7d32;
}
.role-badge--superAdmin {
  background: #ede7f6;
  color: #5e35b1;
}
.role-badge--vendor {
  background: #e3f2fd;
  color: #1565c0;
}
.role-badge--subVendor {
  background: #fff3e0;
  color: #e65100;
}

.per-page {
  width: 100px;
  margin: 0;
}
</style>